import React, { useState, memo } from 'react';
import { List, ChevronDown, ChevronUp, X } from 'lucide-react';
import { TOCItem } from '@/services/blogService';
import { AccessibleButton, useFocusTrap } from './AccessibilityUtils';
import { TouchButton, useResponsive } from './ResponsiveUtils';

interface MobileTableOfContentsProps {
  tocItems: TOCItem[];
  activeId: string;
  onItemClick: (id: string) => void;
  className?: string;
}

export const MobileTableOfContents: React.FC<MobileTableOfContentsProps> = memo(({
  tocItems,
  activeId,
  onItemClick,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useResponsive();
  const modalRef = useFocusTrap(isOpen);

  if (!isMobile || tocItems.length === 0) {
    return null;
  }

  const handleItemClick = (id: string) => {
    onItemClick(id);
    setIsOpen(false);
  };

  const activeItem = tocItems.find(item => item.id === activeId);

  return (
    <>
      {/* Mobile TOC Toggle Button */}
      <div className={`fixed bottom-20 right-4 z-40 ${className}`}>
        <TouchButton
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg border border-blue-500/20"
          size="lg"
        >
          <List className="w-5 h-5 mr-2" />
          <span className="hidden xs:inline">Contents</span>
        </TouchButton>
      </div>

      {/* Mobile TOC Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal Content */}
          <div 
            ref={modalRef}
            className="absolute bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 rounded-t-2xl max-h-[70vh] overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-toc-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 
                id="mobile-toc-title"
                className="text-white font-semibold text-lg flex items-center gap-2"
              >
                <List className="w-5 h-5 text-blue-400" />
                Table of Contents
              </h3>
              <AccessibleButton
                onClick={() => setIsOpen(false)}
                ariaLabel="Close table of contents"
                variant="ghost"
                className="p-2"
              >
                <X className="w-5 h-5" />
              </AccessibleButton>
            </div>

            {/* Current Section Indicator */}
            {activeItem && (
              <div className="px-4 py-3 bg-blue-600/10 border-b border-white/5">
                <p className="text-blue-300 text-sm font-medium">
                  Current Section
                </p>
                <p className="text-white text-sm truncate">
                  {activeItem.title}
                </p>
              </div>
            )}

            {/* TOC Items */}
            <div className="overflow-y-auto max-h-96 overscroll-contain">
              <nav role="navigation" aria-label="Table of contents">
                <ul className="py-2" role="list">
                  {tocItems.map((item) => (
                    <li key={item.id} role="listitem">
                      <TouchButton
                        onClick={() => handleItemClick(item.id)}
                        className={`
                          w-full text-left px-4 py-3 transition-all duration-200
                          hover:bg-white/10 active:bg-white/20
                          ${activeId === item.id 
                            ? 'bg-blue-600/20 text-blue-300 border-l-4 border-blue-400' 
                            : 'text-white/70'
                          }
                        `}
                        size="sm"
                      >
                        <div
                          style={{ 
                            paddingLeft: `${(item.level - 1) * 16}px`,
                          }}
                          className="flex items-center justify-between"
                        >
                          <span 
                            className={`
                              ${item.level === 1 ? 'font-semibold text-base' : 'text-sm'}
                              ${item.level > 2 ? 'text-white/60' : ''}
                            `}
                          >
                            {item.title}
                          </span>
                          {activeId === item.id && (
                            <ChevronUp className="w-4 h-4 text-blue-400 flex-shrink-0 ml-2" />
                          )}
                        </div>
                      </TouchButton>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-slate-800/50">
              <p className="text-white/60 text-xs text-center">
                Tap any section to navigate • Swipe down to close
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

MobileTableOfContents.displayName = 'MobileTableOfContents';

export default MobileTableOfContents;